// src/api/ringcentral-api.ts
// RingCentral OAuth Integration Service - API Client Implementation
// Handles all API communication with RingCentral OAuth service backend
// RELEVANT FILES: src/types/ringcentral.ts, src/api/index.ts, src/stores/ringcentral.ts

import { api as apiClient } from '@/api/index';
import { API_BASE_URL } from '@/config/api.ts';
import { stage } from '@/config/env.ts';
import type { AmendStaffNameResponse } from '@/types/api';
import type {
  CreateOrganizationRequest,
  IntegrationStatus,
  OAuthCallbackResponse,
  Organization,
  RingCentralAccount,
  RingCentralConnection,
  UserAccountInfo,
  WebhookSubscription,
} from '@/types/ringcentral';
import { RingCentralAPIError } from '@/types/ringcentral';

/**
 * RingCentral API Client Class
 * Integrates with existing axios instance and authentication system
 */
class RingCentralAPI {
  /**
   * Get the base URL for RingCentral service endpoints
   */
  private get baseURL(): string {
    return API_BASE_URL[stage];
  }

  /**
   * Make authenticated API request using existing apiClient
   */
  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    data?: any,
    config?: any,
  ): Promise<T> {
    try {
      // Use full URL to override apiClient's baseURL
      const fullUrl = `${this.baseURL}${endpoint}`;

      const response = await apiClient.request({
        method,
        url: fullUrl,
        data,
        ...config,
      });

      return response.data;
    } catch (error: any) {
      console.error(`❌ RingCentral API ${method} failed:`, endpoint, error);

      // Transform error to RingCentralAPIError
      if (error.response) {
        throw new RingCentralAPIError(
          error.response.status,
          error.response.data?.message || error.message,
          error.response.data?.code,
          error.response.data,
        );
      }

      throw new RingCentralAPIError(0, error.message || 'Network error occurred', 'NETWORK_ERROR');
    }
  }

  // ============ Organization Management ============

  /**
   * Create a new organization in RingCentral service
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await this.request<{
      data: Organization;
    }>('POST', '/organizations', data);

    return response.data;
  }

  /**
   * Get organization details
   */
  async getOrganization(organizationId: string): Promise<Organization> {
    const response = await this.request<{ data: Organization }>(
      'GET',
      `/organizations/${organizationId}`,
    );
    return response.data;
  }

  /**
   * Update organization details
   */
  async updateOrganization(
    organizationId: string,
    data: Partial<CreateOrganizationRequest>,
  ): Promise<Organization> {
    const response = await this.request<{ data: Organization }>(
      'PUT',
      `/organizations/${organizationId}`,
      data,
    );
    return response.data;
  }

  /**
   * Delete organization (removes user-organization relationship)
   */
  async deleteOrganization(organizationId: string): Promise<{
    organizationId: string;
    deletedAt: string;
    organizationDeleted: boolean;
    cascadeDeleted: {
      connections: number;
      userRelations: number;
      oauthStates: number;
    };
  }> {
    const response = await this.request<{
      data: {
        organizationId: string;
        deletedAt: string;
        organizationDeleted: boolean;
        cascadeDeleted: {
          connections: number;
          userRelations: number;
          oauthStates: number;
        };
      };
      requestId: string;
      timestamp: string;
    }>('DELETE', `/organizations/${organizationId}`);

    return response.data;
  }

  /**
   * Get all organizations for current user
   */
  async getUserOrganizations(): Promise<Organization[]> {
    const response = await this.request<{
      data: {
        organizations: Organization[];
        count: number;
      };
    }>('GET', '/organizations');

    return response.data.organizations;
  }

  /**
   * Get current user's account information
   * Returns account type (primary/sub-account) and parent account info
   */
  async getUserAccountInfo(): Promise<UserAccountInfo> {
    const response = await this.request<UserAccountInfo>('GET', '/users/me/account-info');

    return response;
  }

  // ============ OAuth Flow ============

  /**
   * Initiate OAuth authorization flow
   */
  async initiateOAuth(
    organizationId: string,
    accessLevel?: 'account' | 'extension',
  ): Promise<{ authorizationUrl: string; stateId: string }> {
    const response = await this.request<{ data: { authorizationUrl: string } }>(
      'POST',
      '/oauth/initiate',
      { organizationId, accessLevel },
    );

    // Extract stateId from the authorization URL
    const authUrl = new URL(response.data.authorizationUrl);
    const stateId = authUrl.searchParams.get('state');

    if (!stateId) {
      throw new Error('State parameter not found in authorization URL');
    }

    return {
      authorizationUrl: response.data.authorizationUrl,
      stateId,
    };
  }

  /**
   * Check OAuth status using stateId
   */
  async checkOAuthStatus(stateId: string): Promise<{
    status: 'PENDING' | 'SUCCESS' | 'DUPLICATE' | 'FAILED';
    state: string;
    service: string;
    createdAt?: string;
    updatedAt?: string;
    completedAt?: string;
    providerAccountId?: string;
    errorMessage?: string;
    errorCode?: string;
    message?: string;
  }> {
    try {
      const response = await this.request<{
        status: 'PENDING' | 'SUCCESS' | 'DUPLICATE' | 'FAILED';
        state: string;
        service: string;
        createdAt?: string;
        updatedAt?: string;
        completedAt?: string;
        providerAccountId?: string;
        errorMessage?: string;
        errorCode?: string;
        message?: string;
      }>('GET', `/oauth/status/${stateId}`);

      return response;
    } catch (error: any) {
      // Handle 404 NOT_FOUND as expired/not found
      if (error.status === 404) {
        return {
          status: 'FAILED',
          state: stateId,
          service: 'RINGCENTRAL',
          errorMessage: 'OAuth state not found or expired',
        };
      }
      throw error;
    }
  }

  /**
   * Refresh OAuth token
   */
  async refreshOAuthToken(connectionId: string): Promise<OAuthCallbackResponse> {
    return this.request<OAuthCallbackResponse>('POST', `/oauth/refresh/${connectionId}`);
  }

  /**
   * Revoke OAuth token and disconnect
   */
  async revokeOAuthToken(connectionId: string): Promise<void> {
    return this.request<void>('POST', `/oauth/revoke/${connectionId}`);
  }

  // ============ Connection Management ============

  /**
   * Get RingCentral connection for organization
   */
  async getConnection(organizationId: string): Promise<RingCentralConnection> {
    return this.request<RingCentralConnection>('GET', `/connections/${organizationId}`);
  }

  /**
   * Get all connections for current user
   */
  async getUserConnections(): Promise<RingCentralConnection[]> {
    return this.request<RingCentralConnection[]>('GET', '/connections');
  }

  /**
   * Update connection configuration
   */
  async updateConnection(
    connectionId: string,
    data: Partial<RingCentralConnection>,
  ): Promise<RingCentralConnection> {
    return this.request<RingCentralConnection>('PUT', `/connections/${connectionId}`, data);
  }

  /**
   * Delete connection
   */
  async deleteConnection(connectionId: string): Promise<void> {
    return this.request<void>('DELETE', `/connections/${connectionId}`);
  }

  /**
   * Test connection status
   */
  async testConnection(connectionId: string): Promise<IntegrationStatus> {
    return this.request<IntegrationStatus>('POST', `/connections/${connectionId}/test`);
  }

  // ============ RingCentral Account Information ============

  /**
   * Get RingCentral account information
   */
  async getAccountInfo(connectionId: string): Promise<RingCentralAccount> {
    return this.request<RingCentralAccount>('GET', `/connections/${connectionId}/account`);
  }

  /**
   * Get RingCentral extensions
   */
  async getExtensions(connectionId: string): Promise<any[]> {
    return this.request<any[]>('GET', `/connections/${connectionId}/extensions`);
  }

  /**
   * Get call log from RingCentral
   */
  async getCallLog(
    connectionId: string,
    params?: {
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      perPage?: number;
    },
  ): Promise<any> {
    return this.request<any>('GET', `/connections/${connectionId}/call-log`, null, {
      params,
    });
  }

  /**
   * Get messages from RingCentral
   */
  async getMessages(
    connectionId: string,
    params?: {
      dateFrom?: string;
      dateTo?: string;
      messageType?: string[];
      page?: number;
      perPage?: number;
    },
  ): Promise<any> {
    return this.request<any>('GET', `/connections/${connectionId}/messages`, null, {
      params,
    });
  }

  // ============ Webhook Management ============

  /**
   * Create webhook subscription
   */
  async createWebhookSubscription(
    connectionId: string,
    data: {
      eventFilters: string[];
      deliveryMode: {
        transportType: 'WebHook';
        address: string;
      };
    },
  ): Promise<WebhookSubscription> {
    return this.request<WebhookSubscription>('POST', `/connections/${connectionId}/webhooks`, data);
  }

  /**
   * Get webhook subscriptions
   */
  async getWebhookSubscriptions(connectionId: string): Promise<WebhookSubscription[]> {
    return this.request<WebhookSubscription[]>('GET', `/connections/${connectionId}/webhooks`);
  }

  /**
   * Update webhook subscription
   */
  async updateWebhookSubscription(
    connectionId: string,
    subscriptionId: string,
    data: Partial<WebhookSubscription>,
  ): Promise<WebhookSubscription> {
    return this.request<WebhookSubscription>(
      'PUT',
      `/connections/${connectionId}/webhooks/${subscriptionId}`,
      data,
    );
  }

  /**
   * Delete webhook subscription
   */
  async deleteWebhookSubscription(connectionId: string, subscriptionId: string): Promise<void> {
    return this.request<void>('DELETE', `/connections/${connectionId}/webhooks/${subscriptionId}`);
  }

  // ============ Data Synchronization ============

  /**
   * Trigger manual data sync
   */
  async triggerSync(
    connectionId: string,
    syncType: 'calls' | 'messages' | 'contacts' | 'all' = 'all',
  ): Promise<{ jobId: string; status: string }> {
    return this.request<{ jobId: string; status: string }>(
      'POST',
      `/connections/${connectionId}/sync`,
      { type: syncType },
    );
  }

  /**
   * Get sync status
   */
  async getSyncStatus(
    connectionId: string,
    jobId?: string,
  ): Promise<{
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress?: number;
    lastSyncTime?: string;
    error?: string;
  }> {
    const url = jobId
      ? `/connections/${connectionId}/sync/${jobId}/status`
      : `/connections/${connectionId}/sync/status`;

    return this.request('GET', url);
  }

  // ============ Analytics & Reporting ============

  /**
   * Get connection analytics
   */
  async getConnectionAnalytics(
    connectionId: string,
    params?: {
      dateFrom?: string;
      dateTo?: string;
      metrics?: string[];
    },
  ): Promise<{
    totalCalls: number;
    totalMessages: number;
    syncSuccess: number;
    syncErrors: number;
    lastActivityTime: string;
  }> {
    return this.request('GET', `/connections/${connectionId}/analytics`, null, {
      params,
    });
  }

  /**
   * Get integration health status
   */
  async getHealthStatus(): Promise<{
    status: 'healthy' | 'degraded' | 'down';
    version: string;
    uptime: number;
    activeConnections: number;
  }> {
    return this.request('GET', '/health');
  }

  /**
   * Fetch calls using V2 API endpoint
   */
  async fetchCallsV2(
    orgId: string,
    params: {
      startDate: string;
      endDate: string;
    },
    options?: { signal?: AbortSignal },
  ): Promise<CallsV2Response> {
    return this.request('GET', '/v2/calls', null, {
      params: {
        orgId,
        startDate: params.startDate,
        endDate: params.endDate,
      },
      signal: options?.signal,
    });
  }

  /**
   * Fetch single call details using V2 API endpoint
   */
  async fetchCallDetailV2(
    telephonySessionId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CallDetailV2Response> {
    return this.request('GET', `/v2/calls/${telephonySessionId}`, null, {
      signal: options?.signal,
    });
  }

  /**
   * Get audio URL for a specific call
   * Returns presigned S3 URL if available and no PII detected
   */
  async getCallAudioUrl(
    telephonySessionId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CallAudioUrlResponse> {
    return this.request('GET', `/v2/call/url`, null, {
      params: { callId: telephonySessionId },
      signal: options?.signal,
    });
  }

  /**
   * Amend staff name in a call record
   */
  async amendCallStaffName(
    telephonySessionId: string,
    newStaffName: string,
    reason?: string,
  ): Promise<AmendStaffNameResponse> {
    const response = await this.request<{
      success: boolean;
      data: AmendStaffNameResponse;
    }>('PUT', `/v2/calls/${telephonySessionId}/amend-staff-name`, {
      newStaffName,
      reason,
    });

    return response.data;
  }

  /**
   * Fetch lead calls data for a specific organization and date range
   */
  async fetchLeadCalls(
    orgId: string,
    startDate: string,
    endDate: string,
  ): Promise<LeadCallsResponse> {
    return this.request<LeadCallsResponse>('GET', '/v2/lead/calls', null, {
      params: {
        orgId,
        startDate,
        endDate,
      },
    });
  }
}

/**
 * V2 Calls API Response Types
 */
export interface CallV2 {
  telephonySessionId: string;
  sessionId: string;
  recordingId: string;
  callStartTime: string;
  callStartTimeDisplay: string;
  callDuration: number;
  callDirection: string;
  callType: string;
  callAction: string;
  callResult: string;
  fromName?: string;
  fromPhoneNumber: string;
  fromExtensionId?: string;
  toName?: string;
  toPhoneNumber: string;
  toLocation?: string;
  staff_name: string;
  customer_type: CustomerType;
  primary_category: PrimaryCategory;
  primary_subcategory: string;
  primary_outcome: OutcomeResult;
  call_state: CallState;
  follow_up_needed: FollowUpNeeded;
  follow_up_reasons: string[];
  credit_card_captured: CreditCardStatus;
  revenue_priority: RevenuePriority;
  executive_summary: string;
  secondary_topics: string[];
  franchise: string;
  siteId: string;
  accountId: string;
  serverId: string;
  s3AudioPath: string;
  s3TranscriptPath: string;
  s3AnalysisPath: string;
  createdAt: string;
  analysisCompletedAt: string;
  ttl: number;
}

export interface CallsV2Response {
  data: {
    calls: CallV2[];
    count: number;
    orgId: string;
    franchise: string;
    timezone: string;
    dateRange: {
      startDate: string;
      endDate: string;
    };
  };
  requestId: string;
  timestamp: string;
}

/**
 * AI Analysis structures for Call Detail V2
 */

// Enum types matching the AI analysis spec
export type CallState =
  | 'human_conversation'
  | 'voicemail'
  | 'no_answer'
  | 'busy_signal'
  | 'system_error'
  | 'No Transcript'
  | 'No Recording';
export type CustomerType =
  | 'prospective_client'
  | 'existing_member'
  | 'former_member'
  | 'returning_visitor'
  | 'other';
export type PrimaryCategory = 'revenue_impacting' | 'service' | 'scheduling' | 'other';
export type OutcomeResult = 'success' | 'attempted' | 'na';
export type CreditCardStatus = 'success' | 'attempted' | 'na';
export type RevenuePriority = 'high' | 'medium' | 'low' | 'none';
export type FollowUpNeeded = 'yes' | 'no';

export interface AICallAnalysis {
  call_state: CallState;
  staff_name: string;
  staff_confidence: number;
}

export interface CustomerProfile {
  type: CustomerType;
  confidence: number;
  evidence: string;
}

export interface PrimaryTopic {
  category: PrimaryCategory;
  subcategory: string;
  outcome: {
    result: OutcomeResult;
    reasoning: string;
  };
  credit_card_captured: CreditCardStatus;
  evidence: string;
}

export interface FollowUp {
  needed: FollowUpNeeded;
  reason: string[];
}

export interface PracticalCoaching {
  scenario_identified: string;
  detailed_feedback_items: {
    timestamp: string;
    what_was_said: string;
    context: string;
    what_could_have_been_said: string;
    why_recommendation_is_better: string;
  };
}

export interface AIAnalysisData {
  ai_call_analysis: AICallAnalysis;
  customer_profile: CustomerProfile;
  primary_topic: PrimaryTopic;
  secondary_topics: string[];
  revenue_priority: RevenuePriority;
  revenue_priority_evidence: string;
  summary: string;
  follow_up: FollowUp;
  practical_coaching: PracticalCoaching;
}

/**
 * Transcript structures
 */
export interface AudioSegment {
  id: number;
  transcript: string;
  start_time: string;
  end_time: string;
  speaker_label: string;
  items: number[];
}

export interface TranscriptData {
  audio_segments: AudioSegment[] | null;
}

/**
 * Audio data
 */
export interface AudioData {
  url: string | null;
  expiresAt: string | null;
}

/**
 * Call metadata
 */
export interface CallMetadata {
  telephonySessionId: string;
  franchise: string;
  siteId?: string;
  callStartTime: string;
  callDuration: number;
  retrievedAt: string;
}

/**
 * Complete call detail response
 */
export interface CallDetailV2Response {
  data: {
    ai: AIAnalysisData | null;
    provider: CallV2;
    transcript: TranscriptData;
    audio: AudioData;
    metadata: CallMetadata;
  };
  requestId: string;
  timestamp: string;
}

/**
 * Call audio URL response
 */
export interface CallAudioUrlResponse {
  data: {
    url: string | null;
    expiresAt: string | null;
    hasPII: boolean;
    reason: string | null;
    telephonySessionId: string;
    franchise: string;
    retrievedAt: string;
  };
  requestId: string;
  timestamp: string;
}

/**
 * Lead Call API Response Types
 */
export interface LeadCallData {
  callId: string;
  startTime: string;
  duration: number;
  direction: 'Inbound' | 'Outbound';
  recordingUrl: string;
  fromPhoneNumber: string;
  toPhoneNumber: string;
}

export interface Lead {
  leadId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  receivedAt: string;
  orgId: string;
  history: LeadCallData[];
}

export interface LeadCallsSummary {
  totalLeads: number;
  calledLeads: number;
  uncalledLeads: number;
}

export interface LeadCallsResponse {
  data: {
    organizationId: string;
    timezone: string;
    startDate: string;
    endDate: string;
    leads: Lead[];
    summary: LeadCallsSummary;
  };
  requestId: string;
  timestamp: string;
}

/**
 * Singleton instance of RingCentral API client
 */
export const ringCentralAPI = new RingCentralAPI();

/**
 * Default export for compatibility
 */
export default ringCentralAPI;

/**
 * Named exports for specific functionality
 */
export { RingCentralAPI };

/**
 * Utility function to check if error is RingCentralAPIError
 */
export function isRingCentralAPIError(error: any): error is RingCentralAPIError {
  return error instanceof RingCentralAPIError || error.name === 'RingCentralAPIError';
}

/**
 * Utility function to format API errors for user display
 */
export function formatRingCentralError(error: any): string {
  if (isRingCentralAPIError(error)) {
    switch (error.status) {
      case 400:
        return error.message || 'Invalid request parameters';
      case 401:
        return 'Authentication failed. Please reconnect your RingCentral account.';
      case 403:
        return 'Insufficient permissions. Please check your RingCentral account access.';
      case 404:
        return 'Resource not found. The requested connection may have been removed.';
      case 429:
        return 'Too many requests. Please wait before trying again.';
      case 500:
        return 'Server error occurred. Please try again later.';
      default:
        return error.message || 'An unexpected error occurred';
    }
  }

  if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
    return 'Network connection failed. Please check your internet connection.';
  }

  return error.message || 'An unknown error occurred';
}
